




import Enumerable from "linq";
import { Mixins, Component, Prop } from "vue-property-decorator";
import { ModelEditableMixin, ModalPresentableMixin } from "../../../../../../base/components/system";
import { ObjectUtility } from "../../../../../../base/utilities";
import { FormatPageStructureEditableMixin, QuestionnaireGuidAssignableMixin } from "../../mixins";
import {
  FormatPageStructureEditor,
  IFormatObjectEditable,
  IEditableFormatObject,
} from "../../../../../domain/editableFormat";
import { ScriptObjectEditor } from "../../../../../domain/editableFormat/objectEditors";
import { IScriptObject, instanceOfIScriptObject } from "../../../../../domain/editableFormat/objects";
import { instanceOfIScriptGroupKeyPropertyEditable } from "../../../../../domain/editableFormat/properties";
import { ObjectIdPublisher } from ".";

@Component
export default class ScriptGroupEditor extends Mixins(ModelEditableMixin, ModalPresentableMixin, FormatPageStructureEditableMixin, QuestionnaireGuidAssignableMixin) {
  // ModelEditableMixinと同時に使う事.ModelEditableMixin利用APIの宣言.
  writePropertyWhenValid: (writer: (source: FormatPageStructureEditor) => void) => void;
  readPropertyOrDefault: <T>(reader: (source: FormatPageStructureEditor) => T, defaultValue: T) => T;

  @Prop()
  publisher: ObjectIdPublisher;

  getSelectedGroupKeys(): string[] {
    return this.extractGroupKeys(this.selectedObjects);
  }

  extractGroupKeys(objects: (IFormatObjectEditable | IEditableFormatObject)[]): string[] {
    return Enumerable.from(objects)
      .select(o => this.toGroupKey(o))
      .where(o => !!o)
      .distinct()
      .toArray();
  }

  toGroupKey(object: IFormatObjectEditable | IEditableFormatObject): string {
    return instanceOfIScriptGroupKeyPropertyEditable(object)
      ? object.scriptGroupKey().getValue()
      : (instanceOfIScriptObject(object) ? object.name : null);
  }

  updateKey(key: string, value: string) {
    this.writePropertyWhenValid(s => {
      Enumerable.from(this.objects)
        .select(o => o instanceof ScriptObjectEditor ? o : null)
        .where(o => !!o && o.scriptGroupKey().getValue() === key)
        .forEach(o => {
          o.scriptGroupKey().setValue(value);
          s.updateObject(o);
        });
    });
  }

  updateEditor(key: string, rowSize: number, columnSize: number) {
    this.writePropertyWhenValid(s => {
      const editor = this.getRootScriptObjectEditor(key);
      editor.tableSize().setValue({ row: rowSize, column: columnSize });
      s.updateObject(editor);
      this.shrinkEditor(s, key, rowSize, columnSize);
      this.addNewExternalDataObject(s, editor, rowSize, columnSize);
      s.deselectAllObjects();
      s.selectObjects(editor);
    });
  }

  private shrinkEditor(source: FormatPageStructureEditor, key: string, rowSize: number, columnSize: number) {
    Enumerable.from(this.objects.concat().reverse())  // 反転してから削除
      .select(o => o instanceof ScriptObjectEditor ? o : null)
      .where(o => !!o && o.scriptGroupKey().getValue() === key &&
        (o.tableIndex().getValue().row > rowSize - 1 || o.tableIndex().getValue().column > columnSize - 1))
      .forEach(o => source.removeObject(o));
  }

  private getRootScriptObjectEditor(key: string): ScriptObjectEditor {
    return Enumerable.from(this.objects)
      .select(o => o instanceof ScriptObjectEditor ? o : null)
      .firstOrDefault(o => !!o &&
        o.scriptGroupKey().getValue() === key &&
        o.tableIndex().getValue().row === 0 &&
        o.tableIndex().getValue().column === 0);
  }

  private addNewExternalDataObject(source: FormatPageStructureEditor, editor: ScriptObjectEditor, rowSize: number, columnSize: number) {
    const tableIndexes = Enumerable.from(this.objects)
      .select(o => o instanceof ScriptObjectEditor ? o : null)
      .where(o => !!o && o.scriptGroupKey().getValue() === editor.scriptGroupKey().getValue())
      .select(o => o.tableIndex().getValue());
    for (let r = 0; r < rowSize; r++) {
      for (let c = 0; c < columnSize; c++) {
        if (tableIndexes.any(e => e.row === r && e.column === c)) {
          continue;
        }
        const object = this.createNewExternalDataObject(editor, r, c);
        this.assignNewGuidForQuestionnaire(object); // Questionnaire拡張使用時GUIDをアサインする.
        source.addObject(object);
      }
    }
  }

  createNewExternalDataObject(editor: ScriptObjectEditor, rowIndex: number, columnIndex: number): IFormatObjectEditable {
    const source = ObjectUtility.copyDeeply(editor.getSource()) as IScriptObject;
    source.id = this.publisher.publish();
    const location = editor.location().getValue();
    const size = editor.size().getValue();
    source.location.x = columnIndex === 0
      ? location.x
      : location.x + (size.width * columnIndex) + 3;
    source.location.y = rowIndex === 0
      ? location.y
      : location.y + (size.height * rowIndex) + 3;
    source.size = size;
    const isOrigin = (rowIndex === 0) && (columnIndex === 0);
    source.scriptTableBehavior = {
      index: {
        row: rowIndex,
        column: columnIndex,
      },
      size: isOrigin
        ? source.scriptTableBehavior.size
        : undefined,
      scriptExecuteBehavior: isOrigin
        ? source.scriptTableBehavior.scriptExecuteBehavior
        : undefined,
    };
    return this.readPropertyOrDefault(s => s.createNewObject(source), null);
  }

  containsSameKey(key: string): boolean {
    return Enumerable.from(this.objects)
      .select(o => o instanceof ScriptObjectEditor ? o : null)
      .any(o => !!o && o.scriptGroupKey().getValue() === key);
  }

  createUniqueKey(key: string, index: number = 1): string {
    const newKey = key + "(" + index + ")";
    return this.containsSameKey(newKey)
      ? this.createUniqueKey(key, index + 1)
      : newKey;
  }

  async alertEmptyKey(): Promise<void> {
    try {
      this.beginModal();
      await this.$alert(
        "外部データ名称を指定してください。",
        "外部データ名称未指定",
        {
          type: "warning"
        }
      );
    } catch {
    } finally {
      this.endModal();
    }
  }

  async confirmCreateUniqueKey(): Promise<boolean> {
    try {
      this.beginModal();
      const index = await this.$commonDialogs.confirmation.confirmYesNo(
        "外部データ名称生成確認",
        ["同一名称の外部データオブジェクトが存在します。連番を付与して生成しますか？"]
      );
      return (index === 0);
    } catch {
      return false;
    } finally {
      this.endModal();
    }
  }
}
